import type { CloudLinkStatus } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import { LoaderCircle, LogIn, Unplug } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SettingRow, SettingsGroup } from '@/components/settings-page/setting-controls';
import {
  loadCloudLink,
  notifyCloudLinkChanged,
  startCloudLink,
  unlinkCloud,
} from '@/lib/cloud-link';
import { errorMessage } from '@/lib/error-message';

const POLL_MS = 2_000;
const SIGN_IN_TIMEOUT_MS = 10 * 60_000;

/**
 * The link of this server to Crowdex Cloud: sign in there from the browser to send
 * conversations to the cloud and see the ones running there. The cloud then keeps your
 * connections. Shown only by a server that links to a cloud (`CROWDEX_CLOUD_URL`).
 */
export function CloudLinkSettings() {
  const [status, setStatus] = useState<CloudLinkStatus | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Whether it was linked at the last check; null before the first one. */
  const linkedRef = useRef<boolean | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await loadCloudLink();
      const linked = next.account !== null;
      if (linkedRef.current !== null && linkedRef.current !== linked) notifyCloudLinkChanged();
      linkedRef.current = linked;
      setStatus(next);
      if (next.account !== null) setSigningIn(false);
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The approval happens in the browser: follow it until the link is saved.
  useEffect(() => {
    if (!signingIn) return;
    const poll = window.setInterval(() => void refresh(), POLL_MS);
    const timeout = window.setTimeout(() => setSigningIn(false), SIGN_IN_TIMEOUT_MS);
    return () => {
      window.clearInterval(poll);
      window.clearTimeout(timeout);
    };
  }, [signingIn, refresh]);

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  if (status?.url == null && error === null) return null;
  const host = status?.url == null ? '' : new URL(status.url).host;
  const account = status?.account ?? null;
  // Linking sends the cloud this computer's name, which lists it among the account's computers.
  const machineNote = status?.machineName
    ? ` This computer is listed there as “${status.machineName}”.`
    : '';

  return (
    <SettingsGroup title="Link to Crowdex Cloud">
      <SettingRow
        description={
          account === null
            ? `Sign in to ${host} to send conversations to the cloud and keep your connections there.${machineNote}`
            : `Signed in to ${host} as ${account.email}. Conversations you send to the cloud run there.${machineNote}`
        }
        title={account === null ? 'Not linked' : account.name || account.email}
      >
        {account === null ? (
          <Button
            className="gap-2"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await startCloudLink();
                setSigningIn(true);
              })
            }
            size="sm"
            type="button"
          >
            {busy || signingIn ? (
              <LoaderCircle className="size-3.5 animate-spin" />
            ) : (
              <LogIn className="size-3.5" />
            )}
            {signingIn ? 'Waiting for the browser…' : 'Sign in to Crowdex Cloud'}
          </Button>
        ) : (
          <Button
            className="gap-2"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await unlinkCloud();
                await refresh();
              })
            }
            size="sm"
            type="button"
            variant="outline"
          >
            {busy ? (
              <LoaderCircle className="size-3.5 animate-spin" />
            ) : (
              <Unplug className="size-3.5" />
            )}
            Sign out of the cloud
          </Button>
        )}
      </SettingRow>
      {error && <p className="text-destructive text-xs">{error}</p>}
    </SettingsGroup>
  );
}
